<template>
  <div>
    <section class="space-y-4">
      <div class="flex items-center space-x-10" v-if="!booking?.arrivedAt">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">Set visitor as arrived</p>
          <p class="text-background-fg/50 text-xs">
            This will set the arrival time to now, and send out the arrival
            SMSs.
          </p>
        </div>

        <button
          class="ring-secondary text-secondary hover:bg-secondary/10 rounded px-2 py-1 ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-inherit"
          type="button"
          :disabled="!!booking?.arrivedAt || isCancelling || isDeleting"
          @click="handleCheckInClick"
        >
          Set Arrived
        </button>
      </div>

      <div class="flex items-center space-x-10" v-else>
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">
            Undo arrival for this booking
          </p>
          <p class="text-background-fg/50 text-xs">
            Undo the arrival for this booking if it was set in error.
          </p>
        </div>

        <button
          class="ring-amber text-amber hover:bg-amber/10 rounded px-2 py-1 ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-inherit"
          type="button"
          :disabled="
            !booking?.arrivedAt || isUnArriving || isCancelling || isDeleting
          "
          @click="handleUnArriveClick"
        >
          Undo arrival
        </button>
      </div>

      <div class="flex items-center space-x-10">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">Manually set as departed</p>
          <p class="text-background-fg/50 text-xs">
            Use when a guest has left but no departure event was captured.
          </p>
        </div>

        <button
          class="ring-primary text-primary hover:bg-primary/10 rounded px-2 py-1 ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-inherit"
          type="button"
          :disabled="
            !booking?.arrivedAt ||
            booking?.manuallyDeparted ||
            booking?.status === 'departed' ||
            isDeparting ||
            isCancelling ||
            isDeleting
          "
          @click="handleDepartClick"
        >
          Mark Departed
        </button>
      </div>

      <div class="flex items-center space-x-10" v-if="canSendCompanionLink">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">Send the companion link</p>
          <p class="text-background-fg/50 text-xs">
            Texts a fresh Arrival Companion login link. Use when the member
            never received theirs, or the one they have has expired.
          </p>
        </div>

        <button
          class="ring-secondary text-secondary hover:bg-secondary/10 rounded px-2 py-1 ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-inherit"
          type="button"
          :disabled="
            !booking?.customer?.phone ||
            isSendingCompanionLink ||
            isCancelling ||
            isDeleting
          "
          @click="handleCompanionLinkClick"
        >
          Send Companion Link
        </button>
      </div>

      <div class="flex items-center space-x-10">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">Cancel this booking</p>
          <p v-if="isFeedBooking" class="text-background-fg/50 text-xs">
            This booking comes from a booking feed. Cancel it there; the next
            import would undo a cancellation made here.
          </p>
          <p v-else class="text-background-fg/50 text-xs">
            This will cancel the booking (reference shown above). This action
            cannot be undone.
          </p>
        </div>

        <button
          class="ring-error text-error hover:bg-error/10 rounded px-2 py-1 ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-inherit"
          type="button"
          :disabled="
            booking?.status === 'cancelled' || isCancelling || isFeedBooking
          "
          @click="handleCancelClick"
        >
          Cancel Booking
        </button>
      </div>

      <div class="flex items-center space-x-10">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">Delete this booking</p>
          <p class="text-background-fg/50 text-xs">
            This will delete the booking (reference shown above). This action
            cannot be undone.
          </p>
        </div>

        <button
          class="ring-error text-error hover:bg-error/10 rounded px-2 py-1 ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-inherit"
          type="button"
          :disabled="isCancelling || isDeleting"
          @click="handleDeleteClick"
        >
          Delete Booking
        </button>
      </div>
    </section>
  </div>

  <BaseConfirmation
    ref="checkinConfirmDialogRef"
    description="Arrive this booking and send pitch request SMS"
    confirm-label="Check In"
    cancel-label="Cancel"
    type="positive"
  />

  <BaseConfirmation
    ref="unArriveConfirmDialogRef"
    description="Are you sure you wish to undo arrival for this booking? This will clear the arrival time and mark any pitch request messages as ignored."
    confirm-label="Yes, undo arrival"
    cancel-label="No, don't undo arrival"
    type="negative"
  />

  <BaseConfirmation
    ref="departConfirmDialogRef"
    description="Mark this booking as departed now?"
    confirm-label="Depart"
    cancel-label="Cancel"
    type="negative"
  />

  <BaseConfirmation
    ref="companionLinkConfirmDialogRef"
    description="Text this member a fresh Arrival Companion login link? The link is single-use and expires after 2 hours."
    confirm-label="Send link"
    cancel-label="Cancel"
    type="positive"
  />

  <BaseConfirmation
    ref="cancelConfirmDialogRef"
    description="Are you sure you wish to cancel this booking?"
    confirm-label="Yes, cancel booking"
    cancel-label="No, don't cancel booking"
  />

  <BaseConfirmation
    ref="deleteConfirmDialogRef"
    description="Are you sure you wish to delete this booking?"
    confirm-label="Delete Booking"
    cancel-label="Cancel"
  />
</template>

<script setup lang="ts">
import {
  type Booking,
  FEED_BOOKING_SOURCES,
} from "@/services/bookings/bookings";
import type BaseConfirmationVue from "./BaseConfirmation.vue";
import { unArriveBooking } from "@/services/bookings/unArriveBooking";
import { checkInBooking } from "@/services/bookings/checkInBooking";
import { cancelBooking } from "@/services/bookings/cancelBooking";
import { deleteBooking } from "@/services/bookings/deleteBooking";
import { departBooking } from "@/services/bookings/departBooking";
import { sendCompanionLink } from "@/services/bookings/sendCompanionLink";
import { useSites } from "@/services/locations/listSites";

const props = defineProps<{
  booking: Booking;
}>();

const router = useRouter();

// Resolve off the booking's own site rather than the header's selected site, so
// the action is right when the modal is opened from an "All sites" list.
// Experience Freedom is excluded to match the endpoint, which rejects it: those
// arrivals sit outside the companion journey and its reports.
const { data: sites } = useSites();
const canSendCompanionLink = computed(
  () =>
    (sites.value?.find((site) => site.id === props.booking?.siteId)
      ?.isCompanion ??
      false) &&
    props.booking?.bookingType !== "experience_freedom",
);
const isFeedBooking = computed(
  () =>
    props.booking?.source != null &&
    FEED_BOOKING_SOURCES.includes(props.booking.source),
);
const deleteConfirmDialogRef = ref<InstanceType<
  typeof BaseConfirmationVue
> | null>(null);

const isDeleting = ref(false);
const handleDeleteClick = async () => {
  if (deleteConfirmDialogRef.value === null) return;

  const { isCanceled: isDeleteCancelled } =
    await deleteConfirmDialogRef.value.reveal();

  if (isDeleteCancelled) return;

  isDeleting.value = true;
  try {
    await deleteBooking(props.booking.id);
    toast.success("Booking deleted");
    router.push({
      name: "bookings",
    });
  } catch (e) {
    toast.error("Failed to delete booking");
    throw e;
  } finally {
    isDeleting.value = false;
  }
};

const cancelConfirmDialogRef = ref<InstanceType<
  typeof BaseConfirmationVue
> | null>(null);
const isCancelling = ref(false);
const handleCancelClick = async () => {
  if (cancelConfirmDialogRef.value === null) return;

  const { isCanceled: isCancelCancelled } =
    await cancelConfirmDialogRef.value.reveal();

  if (isCancelCancelled) return;

  isCancelling.value = true;
  try {
    await cancelBooking(props.booking.id);
    toast.success("Booking cancelled");
    router.push({
      name: "bookings",
    });
  } catch (e) {
    toast.error("Failed to cancel booking");
    throw e;
  } finally {
    isCancelling.value = false;
  }
};

const checkinConfirmDialogRef = ref<InstanceType<
  typeof BaseConfirmationVue
> | null>(null);
const isCheckingIn = ref(false);
const handleCheckInClick = async () => {
  if (checkinConfirmDialogRef.value === null) return;

  const { isCanceled: isCheckinCancelled } =
    await checkinConfirmDialogRef.value.reveal();
  if (isCheckinCancelled) return;

  isCheckingIn.value = true;
  try {
    await checkInBooking(props.booking.id);
    toast.success("Booking checked in");
  } catch (e) {
    toast.error("Failed to check in booking");
    throw e;
  } finally {
    isCheckingIn.value = false;
  }
};

const unArriveConfirmDialogRef = ref<InstanceType<
  typeof BaseConfirmationVue
> | null>(null);
const isUnArriving = ref(false);
const handleUnArriveClick = async () => {
  if (unArriveConfirmDialogRef.value === null) return;

  const { isCanceled: isUnArriveCancelled } =
    await unArriveConfirmDialogRef.value.reveal();
  if (isUnArriveCancelled) return;

  isUnArriving.value = true;
  try {
    await unArriveBooking(props.booking.id);
    toast.success("Booking arrival undone");
  } catch (e) {
    toast.error("Failed to un-arrive booking");
    throw e;
  } finally {
    isUnArriving.value = false;
  }
};

const companionLinkConfirmDialogRef = ref<InstanceType<
  typeof BaseConfirmationVue
> | null>(null);
const isSendingCompanionLink = ref(false);
const handleCompanionLinkClick = async () => {
  if (companionLinkConfirmDialogRef.value === null) return;

  const { isCanceled: isCompanionLinkCancelled } =
    await companionLinkConfirmDialogRef.value.reveal();
  if (isCompanionLinkCancelled) return;

  isSendingCompanionLink.value = true;
  try {
    await sendCompanionLink(props.booking.id);
    toast.success("Companion link sent");
  } catch (e) {
    toast.error("Failed to send companion link");
    throw e;
  } finally {
    isSendingCompanionLink.value = false;
  }
};

const departConfirmDialogRef = ref<InstanceType<
  typeof BaseConfirmationVue
> | null>(null);
const isDeparting = ref(false);
const handleDepartClick = async () => {
  if (departConfirmDialogRef.value === null) return;

  const { isCanceled: isDepartCancelled } =
    await departConfirmDialogRef.value.reveal();
  if (isDepartCancelled) return;

  isDeparting.value = true;
  try {
    await departBooking(props.booking.id);
    toast.success("Booking marked as departed");
  } catch (e) {
    toast.error("Failed to mark booking as departed");
    throw e;
  } finally {
    isDeparting.value = false;
  }
};
</script>
